import {
  Dialog,
  InteractiveContainer,
  PhoneInput,
  PlainButton,
  TextInput,
} from "@jugl-web/ui-components";
import React, { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { cx, useTranslations } from "@jugl-web/utils";
import { LoginMethod } from "@web-src/modules/auth/pages/Login/types";
import { UtilityTextInputBase } from "@jugl-web/ui-components/cross-platform/forms/UtilityTextInputBase";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@jugl-web/utils/consts";
import {
  OTPVerificationForm,
  PageLoaderFull,
} from "@jugl-web/ui-components/cross-platform";
import { CountryPicker } from "@jugl-web/ui-components/web";
import { CountryPickerValue } from "@jugl-web/ui-components/web/CountryPicker/types";
import { isValidEmail } from "@jugl-web/utils/validators";
import ReCAPTCHA from "react-google-recaptcha";
import { Portal } from "@headlessui/react";
import { environment } from "@web-src/environments/environment";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { CX_BUSINESS_NAME_KEY, getStorageItem } from "@jugl-web/utils/storage";
import {
  MAX_NAME_LENGTH,
  MAX_WORKSPACE_NAME_LENGTH,
} from "@web-src/modules/auth/conts";
import { Button } from "@jugl-web/ui-kit";
import { useDefaultCountry } from "../../hooks/useDefaultCountry";
import { ReactComponent as GoogleIcon } from "./assets/google.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";
import instagramShadowImage from "./assets/instagram-shadow.png";
import instagramGhostImage from "./assets/instagram-ghost.png";
import whatsappShadowImage from "./assets/whatsapp-shadow.png";
import whatsappGhostImage from "./assets/whatsapp-ghost.png";
import webShadowImage from "./assets/web-shadow.png";
import webGhostImage from "./assets/web-ghost.png";
import twilioShadowImage from "./assets/twilio-shadow.png";
import twilioGhostImage from "./assets/twilio-ghost.png";
import { useCXSignupFlow } from "./useCXSignupFlow";

export const CXSignupDialog: React.FC<{
  isOpen: boolean;
  isForced?: boolean;
  onRequestClose: () => void;
}> = ({ isOpen, isForced, onRequestClose }) => {
  const { t } = useTranslations();

  const [currentIndex, setCurrentIndex] = useState(0);

  const {
    step,
    loginMethod,
    email,
    phone,
    isLoading,
    otpError,
    recaptchaRef,
    profile,
    isCredentialsValid,
    setLoginMethod,
    setEmail,
    setPhone,
    requestOtp,
    verifyOtp,
    claimWorkspace,
    initiateGoogleSignup,
    goBackToCredentials,
    reset,
    handleLogin,
  } = useCXSignupFlow();

  const { authApi } = useRestApiProvider();
  const { data: countryCode } = authApi.useRequestCountryQuery();
  const defaultCountry = useDefaultCountry();

  // Workspace form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceCountry, setWorkspaceCountry] =
    useState<CountryPickerValue>();
  const [workspaceEmail, setWorkspaceEmail] = useState("");
  const [isWorkspaceSubmitted, setIsWorkspaceSubmitted] = useState(false);

  // Pre-fill workspace email when moving to workspace step
  useEffect(() => {
    if (step === "workspace") {
      if (loginMethod === LoginMethod.email && email) {
        setWorkspaceEmail(email);
      }
    }
  }, [step, loginMethod, email]);

  // Pre-fill country from API response with getUserISO fallback
  useEffect(() => {
    if (defaultCountry && !workspaceCountry) {
      setWorkspaceCountry(defaultCountry);
    }
  }, [defaultCountry, workspaceCountry]);

  // Auto-update workspace name based on stored business name, profile, or typed firstName
  useEffect(() => {
    const storedBusinessName = getStorageItem<string>(CX_BUSINESS_NAME_KEY);
    if (storedBusinessName) {
      setWorkspaceName(storedBusinessName);
    } else if (profile?.first_name) {
      setWorkspaceName(`${profile.first_name}'s Workspace`);
    } else if (firstName) {
      setWorkspaceName(`${firstName}'s Workspace`);
    }
  }, [profile, firstName]);

  // Auto-claim workspace for existing users with profile
  const [isAutoCreating, setIsAutoCreating] = useState(false);
  const autoCreateAttemptedRef = useRef(false);

  useEffect(() => {
    if (step !== "workspace" || !profile || autoCreateAttemptedRef.current)
      return;
    autoCreateAttemptedRef.current = true;

    const countryIso =
      countryCode?.country && countryCode.country !== "default"
        ? countryCode.country
        : "US";
    const storedBusinessName = getStorageItem<string>(CX_BUSINESS_NAME_KEY);
    const wsName = storedBusinessName || `${profile.first_name}'s Workspace`;
    const billingEmail = loginMethod === LoginMethod.email ? email : null;

    if (!billingEmail) return;

    setIsAutoCreating(true);
    claimWorkspace({
      firstName: profile.first_name,
      lastName: profile.last_name,
      workspaceName: wsName,
      workspaceCountry: countryIso,
      workspaceEmail: billingEmail,
    }).then((success) => {
      if (!success) {
        setIsAutoCreating(false);
      }
    });
  }, [step, profile, countryCode, email, loginMethod, claimWorkspace]);

  const tabs = useMemo<{ id: LoginMethod; title: string }[]>(
    () => [
      {
        id: LoginMethod.email,
        title: t({ id: "common.email", defaultMessage: "Email" }),
      },
      {
        id: LoginMethod.phone,
        title: t({
          id: "common.phone-number",
          defaultMessage: "Phone number",
        }),
      },
    ],
    [t]
  );

  const slides = useMemo(
    () => [
      { ghost: instagramGhostImage, shadow: instagramShadowImage },
      { ghost: whatsappGhostImage, shadow: whatsappShadowImage },
      { ghost: webGhostImage, shadow: webShadowImage },
      { ghost: twilioGhostImage, shadow: twilioShadowImage },
    ],
    []
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const interval = setInterval(() => {
      setCurrentIndex((c) => (c + 1) % slides.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [isOpen, slides.length]);

  const handleClose = useCallback(() => {
    onRequestClose();
    reset();
    setFirstName("");
    setLastName("");
    setWorkspaceName("");
    setWorkspaceCountry(undefined);
    setWorkspaceEmail("");
    setIsWorkspaceSubmitted(false);
  }, [onRequestClose, reset]);

  const handleWorkspaceSubmit = async () => {
    setIsWorkspaceSubmitted(true);
    if (
      (!profile && (!firstName.trim() || !lastName.trim())) ||
      !workspaceName.trim() ||
      !workspaceCountry?.iso ||
      !isValidEmail(workspaceEmail)
    ) {
      return;
    }

    claimWorkspace({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      workspaceName: workspaceName.trim(),
      workspaceCountry: workspaceCountry.iso,
      workspaceEmail,
    });
  };

  const renderCredentialsStep = () => (
    <form
      className="my-auto flex w-[500px] flex-col items-center justify-center py-4"
      onSubmit={requestOtp}
    >
      <span className="text-dark font-secondary text-[32px] font-medium leading-[48px]">
        {isForced
          ? t({
              id: "cx-ai-agents-page.trial-limit-reached",
              defaultMessage: "AI Agents trial limit reached ⚠️",
            })
          : t({
              id: "cx-ai-agents-page.signup-to-proceed",
              defaultMessage: "Sign-up to proceed",
            })}
      </span>
      <span className="text-grey-900 font-secondary mt-2 text-center text-sm leading-4">
        {isForced
          ? t({
              id: "cx-ai-agents-page.guest-limit-message",
              defaultMessage:
                "You've hit the limit for guest users. Simply create an account to pick up right where you left off!",
            })
          : t({
              id: "cx-ai-agents-page.select-signup-method",
              defaultMessage: "Select the sign-up method you like",
            })}
      </span>
      <InteractiveContainer
        className="border-grey-400 hover:border-grey-600 mt-16 flex h-[58px] w-full items-center justify-center gap-2.5 rounded-xl border border-solid px-5 transition-colors"
        onClick={initiateGoogleSignup}
      >
        <GoogleIcon />
        <span className="text-dark font-secondary leading-2 text-xs">
          {t({
            id: "cx-ai-agents-page.signup-with-google",
            defaultMessage: "Sign up with Google",
          })}
        </span>
      </InteractiveContainer>
      <div className="my-7 flex w-full items-center gap-[25px]">
        <div className="bg-dark-100 h-px w-full" />
        <span className="text-dark-700 font-secondary text-xs leading-4">
          {t({ id: "common.or", defaultMessage: "Or" })}
        </span>
        <div className="bg-dark-100 h-px w-full" />
      </div>
      <div className="flex w-full flex-col gap-[22px]">
        <div className="flex items-center gap-2">
          {tabs.map((tab) => (
            <InteractiveContainer
              key={tab.id}
              className={cx(
                "text-dark-700 font-secondary hover:text-dark px-2.5 text-sm transition-colors hover:underline",
                tab.id === loginMethod && "text-dark underline"
              )}
              onClick={() => setLoginMethod(tab.id)}
            >
              {tab.title}
            </InteractiveContainer>
          ))}
        </div>
        {loginMethod === LoginMethod.email ? (
          <TextInput
            placeholder="email@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        ) : (
          <UtilityTextInputBase>
            <PhoneInput
              maxLength={42}
              placeholder="0000000000"
              wrapperClassName="h-[56px] w-full"
              onChange={(phoneValue) => setPhone(phoneValue)}
              value={
                phone.code && phone.iso
                  ? { phone: phone.phone, code: phone.code, iso: phone.iso }
                  : undefined
              }
            />
          </UtilityTextInputBase>
        )}
      </div>
      <Button
        onClick={() => requestOtp()}
        className="mt-12 w-[500px]"
        isDisabled={!isCredentialsValid || isLoading}
        iconEnd={<ArrowIcon />}
      >
        {t({ id: "common.continue", defaultMessage: "Continue" })}
      </Button>
    </form>
  );

  const renderOtpStep = () => (
    <div className="my-auto flex w-[500px] flex-col items-center justify-center py-4">
      <OTPVerificationForm
        title={
          loginMethod === LoginMethod.email
            ? t({
                id: "login-page.enter-code-sent-to-email",
                defaultMessage: "Enter Code Sent to Your Email",
              })
            : t({
                id: "login-page.enter-code-sent-to-number",
                defaultMessage: "Enter Code Sent to Your Number",
              })
        }
        subTitle={
          <div className="flex flex-col items-center gap-[6px]">
            <span className="text-grey-800 text-sm">
              {loginMethod === LoginMethod.email
                ? t(
                    {
                      id: "login-page.we-sent-it-to-email",
                      defaultMessage: "We sent it to the Email {email}",
                    },
                    {
                      email: <span className="text-primary-800">{email}</span>,
                    }
                  )
                : t(
                    {
                      id: "login-page.we-sent-it-to-number",
                      defaultMessage: "We sent it to the Phone Number {number}",
                    },
                    {
                      number: (
                        <span className="text-primary font-medium">{`${phone.code} ${phone.phone}`}</span>
                      ),
                    }
                  )}
            </span>
            <InteractiveContainer
              className="text-grey-900 hover:text-primary-800 w-max px-3 text-sm underline transition-all duration-300 hover:font-medium"
              onClick={goBackToCredentials}
            >
              {loginMethod === LoginMethod.email
                ? t({
                    id: "login-page.change-email",
                    defaultMessage: "Change Email Address",
                  })
                : t({
                    id: "login-page.change-number",
                    defaultMessage: "Change Phone Number",
                  })}
            </InteractiveContainer>
          </div>
        }
        onSubmit={verifyOtp}
        onResend={requestOtp}
        error={otpError}
      />
    </div>
  );

  const renderWorkspaceLimitStep = () => {
    return (
      <div className="my-auto flex flex-col items-center justify-center py-4 text-center">
        <span className="text-dark font-secondary w-[540px] text-[32px] font-medium leading-[48px]">
          {t({
            id: "cx-ai-agents-page.workspace-limit-reached",
            defaultMessage: "You reached Workspaces Limit ⚠️",
          })}
        </span>
        <span className="text-grey-900 font-secondary mt-2 w-[500px] text-sm leading-4">
          {t(
            {
              id: "cx-ai-agents-page.workspace-limit-description",
              defaultMessage:
                "Oh no! Seems like you already created <highlightedText>5 of 5 Personal Workspaces</highlightedText>. Your Agent creation progress is lost. Please manage Workspaces to create a new one and try again",
            },
            {
              highlightedText: (text: ReactNode) => (
                <span className="text-primary-800 font-medium">{text}</span>
              ),
            }
          )}
        </span>
        <Button
          className="mt-16 h-12 w-[300px]"
          onClick={handleLogin}
          iconEnd={<ArrowIcon />}
        >
          {t({
            id: "cx-ai-agents-page.login-to-my-workspace",
            defaultMessage: "Login to my Workspace",
          })}
        </Button>
      </div>
    );
  };

  const workspaceEmailError = useMemo(() => {
    if (!isWorkspaceSubmitted) return undefined;
    if (!workspaceEmail.length) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    if (!isValidEmail(workspaceEmail)) {
      return t({
        id: "form-controls.email.error-is-incorrect",
        defaultMessage: "Email is incorrect",
      });
    }
    return undefined;
  }, [isWorkspaceSubmitted, workspaceEmail, t]);

  const allWorkspaceFieldsHaveValues =
    (!!profile || (!!firstName.trim() && !!lastName.trim())) &&
    !!workspaceName.trim() &&
    !!workspaceCountry?.iso &&
    !!workspaceEmail;

  const renderWorkspaceStep = () => (
    <div className="my-auto flex w-[500px] flex-col items-center justify-center py-4">
      <span className="text-dark font-secondary text-[32px] font-medium leading-[48px]">
        {t({
          id: "cx-ai-agents-page.lets-start-with-essentials",
          defaultMessage: "Let's start with essentials",
        })}
      </span>
      <span className="text-grey-900 font-secondary mt-2 text-sm leading-4">
        {t({
          id: "cx-ai-agents-page.just-a-few-details",
          defaultMessage: "Just a few details to start with",
        })}
      </span>
      <div className="mt-10 flex w-full flex-col gap-6">
        {!profile && (
          <div className="flex gap-4">
            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "common.first-name",
                defaultMessage: "First Name",
              })}
              isRequired
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              isInvalid={isWorkspaceSubmitted && !firstName.trim()}
              classNames={{ wrapperClassName: "flex-1" }}
              maxLength={MAX_NAME_LENGTH}
            />
            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "common.last-name",
                defaultMessage: "Last Name",
              })}
              isRequired
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              isInvalid={isWorkspaceSubmitted && !lastName.trim()}
              classNames={{ wrapperClassName: "flex-1" }}
              maxLength={MAX_NAME_LENGTH}
            />
          </div>
        )}
        <div className="flex gap-4">
          <TextInput
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            label={t({
              id: "create-entity-form.workspace-name",
              defaultMessage: "Workspace Name",
            })}
            isRequired
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            isInvalid={isWorkspaceSubmitted && !workspaceName.trim()}
            classNames={{ wrapperClassName: "flex-1" }}
            maxLength={MAX_WORKSPACE_NAME_LENGTH}
          />
          <CountryPicker
            countryIso={workspaceCountry?.iso}
            onSelect={(item) => setWorkspaceCountry(item)}
            placement="top"
            className="z-[9999]"
          >
            {() => (
              <TextInput
                value={
                  workspaceCountry?.name
                    ? `${workspaceCountry.name} (${workspaceCountry.iso})`
                    : ""
                }
                isRequired
                label={t({
                  id: "create-entity-form.workspace-country",
                  defaultMessage: "Workspace Country",
                })}
                placeholder={t({
                  id: "common.select",
                  defaultMessage: "Select",
                })}
                classNames={{
                  wrapperClassName: "flex-1",
                  inputClassName: "cursor-pointer",
                }}
                isInvalid={isWorkspaceSubmitted && !workspaceCountry?.iso}
                readOnly
              />
            )}
          </CountryPicker>
        </div>
        <TextInput
          placeholder="email@gmail.com"
          label={t({
            id: "create-entity-form.workspace-email",
            defaultMessage: "Workspace Email",
          })}
          isRequired
          value={workspaceEmail}
          onChange={(e) => setWorkspaceEmail(e.target.value)}
          isInvalid={isWorkspaceSubmitted && !!workspaceEmailError}
          errorMessage={workspaceEmailError}
          type="email"
        />
      </div>
      <Button
        className="mt-10 w-[500px]"
        onClick={handleWorkspaceSubmit}
        isDisabled={!allWorkspaceFieldsHaveValues || isLoading}
        isLoading={isLoading}
        iconEnd={<ArrowIcon />}
      >
        {t({
          id: "cx-ai-agents-page.create-workspace",
          defaultMessage: "Create Workspace",
        })}
      </Button>
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={isForced ? () => {} : handleClose}
      className="fixed z-[1400] flex h-full max-h-[min(783px,95vh)] w-full max-w-[min(1310px,95%)] items-center overflow-hidden rounded-2xl bg-white"
    >
      {environment.isReCaptchaEnabled && (
        <Portal>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={environment.reCaptchaKey}
          />
        </Portal>
      )}
      {!isForced && (
        <PlainButton
          className="absolute top-5 right-5 z-10 flex h-8 w-8 items-center justify-center"
          onClick={handleClose}
        >
          <CrossIcon />
        </PlainButton>
      )}
      <div className="bg-dark relative mr-12 hidden h-full w-[476px] shrink-0 [@media(min-width:1170px)_and_(min-height:650px)]:block">
        {slides.map((slide, index) => (
          <div
            key={+index}
            className={cx(
              "pointer-events-none transition-opacity duration-700",
              index === currentIndex ? "opacity-100" : "opacity-0"
            )}
          >
            <img
              src={slide.shadow}
              className="absolute left-0 top-1/2 w-3/4 -translate-y-1/2"
              alt=""
            />
            <img
              src={slide.ghost}
              className="absolute left-0 top-1/2 w-[510px] -translate-y-1/2"
              alt=""
            />
          </div>
        ))}
      </div>
      <div className="jugl__custom-scrollbar flex h-full w-full flex-col items-center justify-between gap-8 overflow-y-auto py-4 px-10">
        {step === "credentials" && renderCredentialsStep()}
        {step === "otp" && renderOtpStep()}
        {step === "workspace" && isAutoCreating && (
          <div className="my-auto flex items-center justify-center">
            <PageLoaderFull />
          </div>
        )}
        {step === "workspace" && !isAutoCreating && renderWorkspaceStep()}
        {step === "workspaceLimit" && renderWorkspaceLimitStep()}
        <span className="text-dark-500 font-secondary text-sm leading-[160%]">
          {t(
            {
              id: "cx-ai-agents-page.terms-and-privacy",
              defaultMessage: `<termsLink>Terms and Conditions</termsLink> and <privacyLink>Privacy Policy</privacyLink>`,
            },
            {
              termsLink: (text: ReactNode) => (
                <a
                  className="text-primary-600 underline"
                  href={TERMS_OF_USE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </a>
              ),
              privacyLink: (text: ReactNode) => (
                <a
                  className="text-primary-600 underline"
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </a>
              ),
            }
          )}
        </span>
      </div>
    </Dialog>
  );
};
